import { Button } from "@/components/ui/button";
import { Save } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { format } from "date-fns";
import { Habit } from "@/context/SupabaseHabitContext";
import { useIsMobile } from "@/hooks/use-mobile";

interface HabitGridHeaderProps {
  habit: Habit;
  totalContributions: number;
  editMode: boolean;
  onNavigate: (direction: 'forward' | 'backward') => void;
  onToggleEditMode: () => void;
  onSave: () => void;
  onCancel: () => void;
}

export default function HabitGridHeader({
  habit,
  totalContributions,
  editMode,
  onNavigate,
  onSave,
  onCancel,
}: HabitGridHeaderProps) {
  const isMobile = useIsMobile();

  return (
    <div className={`flex justify-between items-center mb-4`}>
      <div className="flex items-center">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-8 ml-2 font-normal"
            >
              <span className="font-medium">{totalContributions}×</span> contributions
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80">
            <div className="grid gap-2">
              <h4 className="font-medium leading-none">Contributions</h4>
              <p className="text-sm text-muted-foreground">
                You've completed this habit {totalContributions} times since {format(new Date(habit.createdAt), 'MMMM d, yyyy')}.
              </p>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {isMobile ? (
        <div className="flex items-center gap-1">
          <button
            onClick={() => onNavigate('backward')}
            className="p-1 rounded hover:bg-accent"
            aria-label="Previous months"
            disabled={editMode}
          >
            &larr;
          </button>
          {editMode ? (
            <>
              <Button size="sm" variant="outline" className="h-8" onClick={onCancel}>
                Cancel
              </Button>
              <Button size="sm" className="h-8" onClick={onSave}>
                <Save className="h-4 w-4" />
              </Button>
            </>
          ) : null}
          {!editMode && (
            <button
              onClick={() => onNavigate('forward')}
              className="p-1 rounded hover:bg-accent"
              aria-label="Next months"
            >
              &rarr;
            </button>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2">
          {editMode ? (
            <>
              <Button size="sm" variant="outline" className="h-8 px-3" onClick={onCancel}>
                Cancel
              </Button>
              <Button size="sm" className="h-8 px-3" onClick={onSave}>
                <Save className="h-4 w-4 mr-1" />
                Save
              </Button>
            </>
          ) : (
            <div className="flex items-center gap-1">
              <button
                onClick={() => onNavigate('backward')}
                className="p-1 rounded hover:bg-accent"
                aria-label="Previous months"
                disabled={editMode}
              >
                &larr;
              </button>
              <button
                onClick={() => onNavigate('forward')}
                className="p-1 rounded hover:bg-accent"
                aria-label="Next months"
                disabled={editMode}
              >
                &rarr;
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}